import { signal } from '@preact/signals-core';
import { getAuthHeaders, customersSignal, searchCustomersForAutocomplete } from './assets';

export const plReportSignal = signal<any | null>(null);
export const analyticsLoadingSignal = signal<boolean>(false);
export const analyticsErrorSignal = signal<string | null>(null);
export const selectedAssetPLSignal = signal<any | null>(null);
export const activeTabSignal = signal<string>('fleet');

export const selectedCustomerIdSignal = signal<string>('');
export const startDateSignal = signal<string>('');
export const endDateSignal = signal<string>('');

export function initAnalyticsSignals() {
	if (!startDateSignal.value) {
		const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
		startDateSignal.value = thirtyDaysAgo.toISOString().split('T')[0] || '';
	}
	if (!endDateSignal.value) {
		endDateSignal.value = new Date().toISOString().split('T')[0] || '';
	}
	if (!selectedCustomerIdSignal.value && customersSignal.value.length > 0) {
		selectedCustomerIdSignal.value = customersSignal.value[0].id;
	}
}

export async function runPLReport() {
	const custId = selectedCustomerIdSignal.value;
	const start = startDateSignal.value;
	const end = endDateSignal.value;

	if (!custId || !start || !end) {
		analyticsErrorSignal.value = 'Please select a customer and specify both start and end dates.';
		return;
	}

	analyticsLoadingSignal.value = true;
	analyticsErrorSignal.value = null;

	try {
		const res = await fetch(`/api/v1/analytics/pl?customerId=${custId}&startDate=${start}&endDate=${end}`, {
			headers: getAuthHeaders()
		});
		if (res.ok) {
			plReportSignal.value = await res.json();
		} else {
			const errData = await res.json();
			analyticsErrorSignal.value = errData.error || 'Failed to fetch Profit & Loss report.';
			plReportSignal.value = null;
		}
	} catch (e) {
		console.error('Failed to query PL report', e);
		analyticsErrorSignal.value = 'Network failure.';
		plReportSignal.value = null;
	} finally {
		analyticsLoadingSignal.value = false;
	}
}

export function renderAnalyticsPage(): string {
	const report = plReportSignal.value;
	const loading = analyticsLoadingSignal.value;
	const error = analyticsErrorSignal.value;
	const activeTab = activeTabSignal.value;

	let totalRev = 0;
	let totalExp = 0;
	let netProfit = 0;
	let margin = 0;

	if (report) {
		totalRev = report.totalRevenue || 0;
		totalExp = report.totalExpense || 0;
		netProfit = report.netProfit || 0;
		if (totalRev > 0) {
			margin = (netProfit / totalRev) * 100;
		}
	}

	let profitColor = 'var(--p-color-text-success)';
	if (netProfit < 0) {
		profitColor = 'var(--p-color-text-critical)';
	}

	let marginTone = 'attention';
	if (margin > 20) {
		marginTone = 'success';
	} else if (margin < 0) {
		marginTone = 'critical';
	}

	return `
		<style>
			.tab-button {
				transition: color 0.15s ease, border-bottom-color 0.15s ease;
			}
			.tab-button:hover {
				color: var(--p-color-text-link, #008060) !important;
			}
			.tab-button.active {
				border-bottom-color: var(--p-color-border-accent, #008060) !important;
			}
			.drilldown-title {
				font-size: 1rem;
				font-weight: 600;
				margin-bottom: 0.5rem;
				border-bottom: 1px solid var(--p-color-border-subdued);
				padding-bottom: 0.25rem;
				color: var(--p-color-text-subdued);
				text-transform: uppercase;
				letter-spacing: 0.05em;
			}
			.drilldown-grid {
				display: grid;
				grid-template-columns: 1fr 1fr;
				gap: 1.5rem;
				margin-top: 1rem;
			}
			.drilldown-field {
				display: flex;
				justify-content: space-between;
				padding: 0.5rem 0;
				border-bottom: 1px dotted var(--p-color-border-subdued);
				font-size: 0.9rem;
			}
			.drilldown-total {
				display: flex;
				justify-content: space-between;
				padding: 0.75rem 0;
				font-weight: bold;
				font-size: 1rem;
				border-top: 1px solid var(--p-color-border-subdued);
				margin-top: 0.5rem;
			}
		</style>
		<s-page heading="P&L Analytics Portal" subtitle="Examine print usage allocations, customer margin calculations, and individual machine-level cost distributions." inlineSize="large">
			<!-- Control Panel Section -->
			<s-section>
				<s-box padding="base" background="surface" border="base" borderRadius="base">
					<s-form id="form-run-analytics">
						<s-stack gap="base">
							<div style="display: grid; grid-template-columns: 2fr 1fr 1fr auto; gap: 1rem; align-items: end;">
								<div style="display: flex; flex-direction: column; gap: 0.25rem;">
									<label style="font-size: 0.875rem; font-weight: 500;">Customer Organization</label>
									<opm-autocomplete
										id="analytics-customer-select"
										label="Customer Organization"
										placeholder="Search customers..."
									></opm-autocomplete>
									<input type="hidden" id="analytics-customer-id" name="customerId" value="${selectedCustomerIdSignal.value}" />
								</div>
								
								<s-text-field label="Start Date" id="analytics-start-date" type="date" value="${startDateSignal.value}" required></s-text-field>
								<s-text-field label="End Date" id="analytics-end-date" type="date" value="${endDateSignal.value}" required></s-text-field>
								
								<s-button variant="primary" id="btn-run-analytics" style="height: 38px;">Generate Report</s-button>
							</div>
							${error ? `
								<div style="color: var(--p-color-text-critical); font-size: 0.875rem; font-weight: 500;">${error}</div>
							` : ''}
						</s-stack>
					</s-form>
				</s-box>
			</s-section>

			${!report && !loading ? `
				<s-section>
					<s-box padding="base" background="surface" border="base" borderRadius="base">
						<div style="padding: 4rem; text-align: center; color: var(--p-color-text-subdued);">
							<s-icon type="chart-donut" size="large" style="margin-bottom: 1rem; color: var(--p-color-icon-subdued);"></s-icon>
							<s-heading element="h3">Run P&L Report</s-heading>
							<s-paragraph>Select a customer organization and date period range to populate metrics.</s-paragraph>
						</div>
					</s-box>
				</s-section>
			` : ''}

			${loading ? `
				<s-section>
					<s-box padding="base" background="surface" border="base" borderRadius="base">
						<div style="padding: 4rem; text-align: center;">
							<s-spinner size="large" style="margin-bottom: 1rem;"></s-spinner>
							<s-text tone="subdued">Aggregating counter differences and allocating expense shares...</s-text>
						</div>
					</s-box>
				</s-section>
			` : ''}

			${report && !loading ? `
				<!-- Customer Level Totals Overview -->
				<s-section>
					<div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 1rem;">
						<s-box padding="base" background="surface" border="base" borderRadius="base">
							<s-stack gap="small">
								<s-text tone="subdued">TOTAL REVENUE</s-text>
								<s-heading element="h2" style="font-size: 1.5rem; font-weight: 700; margin: 0;">NRs. ${totalRev.toFixed(2)}</s-heading>
								<s-text style="font-size: 0.75rem; color: var(--p-color-text-success);">Rentals + usage + direct memos</s-text>
							</s-stack>
						</s-box>
						
						<s-box padding="base" background="surface" border="base" borderRadius="base">
							<s-stack gap="small">
								<s-text tone="subdued">TOTAL EXPENSE</s-text>
								<s-heading element="h2" style="font-size: 1.5rem; font-weight: 700; margin: 0;">NRs. ${totalExp.toFixed(2)}</s-heading>
								<s-text style="font-size: 0.75rem; color: var(--p-color-text-subdued);">Direct expenses + allocated share</s-text>
							</s-stack>
						</s-box>
						
						<s-box padding="base" background="surface" border="base" borderRadius="base">
							<s-stack gap="small">
								<s-text tone="subdued">NET PROFIT / LOSS</s-text>
								<s-heading element="h2" style="font-size: 1.5rem; font-weight: 700; margin: 0; color: ${profitColor};">NRs. ${netProfit.toFixed(2)}</s-heading>
								<s-text style="font-size: 0.75rem; color: var(--p-color-text-subdued);">Revenues minus expenses in period</s-text>
							</s-stack>
						</s-box>
						
						<s-box padding="base" background="surface" border="base" borderRadius="base">
							<s-stack gap="small">
								<s-text tone="subdued">PROFIT MARGIN</s-text>
								<div style="display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;">
									<s-heading element="h2" style="font-size: 1.5rem; font-weight: 700; margin: 0;">${margin.toFixed(2)}%</s-heading>
									<s-badge tone="${marginTone}">${margin >= 0 ? 'PROFITABLE' : 'UNPROFITABLE'}</s-badge>
								</div>
								<s-text style="font-size: 0.75rem; color: var(--p-color-text-subdued);">Percentage return on total revenue</s-text>
							</s-stack>
						</s-box>
					</div>
				</s-section>

				<!-- Tab Switches -->
				<s-section>
					<div style="display: flex; gap: 1rem; border-bottom: 1px solid var(--p-color-border-subdued); padding-bottom: 0.5rem; margin-bottom: 1rem;">
						<button id="btn-tab-fleet" class="tab-button ${activeTab === 'fleet' ? 'active' : ''}" style="background: none; border: none; font-size: 0.95rem; font-weight: 600; padding: 0.5rem 1rem; cursor: pointer; color: ${activeTab === 'fleet' ? 'var(--p-color-text-link, #008060)' : 'var(--p-color-text-subdued)'}; border-bottom: 2px solid ${activeTab === 'fleet' ? 'var(--p-color-text-link, #008060)' : 'transparent'};">
							Fleet Assets Comparison
						</button>
						<button id="btn-tab-depts" class="tab-button ${activeTab === 'depts' ? 'active' : ''}" style="background: none; border: none; font-size: 0.95rem; font-weight: 600; padding: 0.5rem 1rem; cursor: pointer; color: ${activeTab === 'depts' ? 'var(--p-color-text-link, #008060)' : 'var(--p-color-text-subdued)'}; border-bottom: 2px solid ${activeTab === 'depts' ? 'var(--p-color-text-link, #008060)' : 'transparent'};">
							Department Cost Centers
						</button>
					</div>

					<!-- Content based on Active Tab -->
					<s-box padding="none" background="surface" border="base" borderRadius="base">
						${activeTab === 'fleet' ? `
							<opm-data-table
								id="analytics-fleet-table"
								searchable="true"
								paginated="true"
								page-size="10"
								empty-message="No fleet assets recorded for this period."
								search-placeholder="Search by serial number..."
							></opm-data-table>
						` : `
							<opm-data-table
								id="analytics-depts-table"
								searchable="true"
								paginated="true"
								page-size="10"
								empty-message="No department records for this period."
								search-placeholder="Search by department name..."
							></opm-data-table>
						`}
					</s-box>
				</s-section>
			` : ''}

			<!-- Machine Detail Modal -->
			<s-modal id="machine-detail-modal" heading="Machine Allocation Detail View">
				<div id="machine-detail-content">
					<!-- Dynamically populated -->
				</div>
				<s-stack direction="inline" gap="small" style="margin-top: 1.5rem; justify-content: flex-end;">
					<s-button commandFor="machine-detail-modal" command="--hide">Close Details</s-button>
				</s-stack>
			</s-modal>
		</s-page>
	`;
}

export function setupAnalyticsPageListeners() {
	const report = plReportSignal.value;
	const activeTab = activeTabSignal.value;

	// 1. Run Report Button Listener
	const btnRun = document.getElementById('btn-run-analytics');
	if (btnRun) {
		btnRun.addEventListener('click', async (e) => {
			e.preventDefault();
			const custAutocomplete = document.getElementById('analytics-customer-select') as any;
			const startInput = document.getElementById('analytics-start-date') as HTMLInputElement;
			const endInput = document.getElementById('analytics-end-date') as HTMLInputElement;

			if (custAutocomplete) selectedCustomerIdSignal.value = custAutocomplete.selectedId || selectedCustomerIdSignal.value;
			if (startInput) startDateSignal.value = startInput.value;
			if (endInput) endDateSignal.value = endInput.value;

			await runPLReport();
			triggerAppReRender();
		});
	}

	const custAutocomplete = document.getElementById('analytics-customer-select') as any;
	if (custAutocomplete) {
		custAutocomplete.loadItems = searchCustomersForAutocomplete;
		custAutocomplete.addEventListener('select', (e: CustomEvent) => {
			selectedCustomerIdSignal.value = e.detail.id;
			const hidden = document.getElementById('analytics-customer-id') as HTMLInputElement | null;
			if (hidden) hidden.value = e.detail.id || '';
		});
	}

	// 2. Tab Listeners
	const btnTabFleet = document.getElementById('btn-tab-fleet');
	if (btnTabFleet) {
		btnTabFleet.addEventListener('click', () => {
			activeTabSignal.value = 'fleet';
			triggerAppReRender();
		});
	}
	const btnTabDepts = document.getElementById('btn-tab-depts');
	if (btnTabDepts) {
		btnTabDepts.addEventListener('click', () => {
			activeTabSignal.value = 'depts';
			triggerAppReRender();
		});
	}

	// 3. Populate and configure tables if data exists
	if (report) {
		if (activeTab === 'fleet') {
			const fleetTable = document.getElementById('analytics-fleet-table') as any;
			if (fleetTable) {
				fleetTable.columns = [
					{ key: 'serialNumber', header: 'Serial Number', sortable: true, sortType: 'string', render: (row: any) => `<strong>${row.serialNumber}</strong>` },
					{ key: 'pagesPrinted', header: 'Pages Printed', sortable: true, sortType: 'number', render: (row: any) => row.pagesPrinted.toLocaleString() },
					{ key: 'totalRevenue', header: 'Total Revenue', sortable: true, sortType: 'number', render: (row: any) => `NRs. ${row.totalRevenue.toFixed(2)}` },
					{ key: 'totalExpense', header: 'Total Expense', sortable: true, sortType: 'number', render: (row: any) => `NRs. ${row.totalExpense.toFixed(2)}` },
					{
						key: 'netProfit',
						header: 'Net Profit',
						sortable: true,
						sortType: 'number',
						render: (row: any) => {
							const color = row.netProfit >= 0 ? 'var(--p-color-text-success)' : 'var(--p-color-text-critical)';
							return `<span style="font-weight: 600; color: ${color};">NRs. ${row.netProfit.toFixed(2)}</span>`;
						}
					},
					{
						key: 'margin',
						header: 'Margin %',
						sortable: true,
						sortType: 'number',
						render: (row: any) => {
							const margin = row.totalRevenue > 0 ? (row.netProfit / row.totalRevenue * 100) : 0;
							const tone = margin >= 20 ? 'success' : (margin < 0 ? 'critical' : 'attention');
							return `<s-badge tone="${tone}">${margin.toFixed(1)}%</s-badge>`;
						}
					},
					{
						key: 'actions',
						header: 'Actions',
						render: (row: any) => `
							<s-button data-action="view-details" data-id="${row.assetId}" size="slim">View Details</s-button>
						`
					}
				];
				fleetTable.items = report.assets || [];

				fleetTable.addEventListener('row-action', (e: CustomEvent) => {
					const { action, id } = e.detail;
					if (action === 'view-details') {
						const assetReport = (report.assets || []).find((a: any) => a.assetId === id);
						if (assetReport) {
							selectedAssetPLSignal.value = assetReport;
							populateMachineDetailModal(assetReport);
							const modal = document.getElementById('machine-detail-modal') as any;
							if (modal && typeof modal.showOverlay === 'function') {
								modal.showOverlay();
							}
						}
					}
				});
			}
		} else {
			const deptsTable = document.getElementById('analytics-depts-table') as any;
			if (deptsTable) {
				deptsTable.columns = [
					{ key: 'departmentName', header: 'Department', sortable: true, sortType: 'string', render: (row: any) => `<strong>${row.departmentName}</strong>` },
					{ key: 'totalRevenue', header: 'Total Revenue', sortable: true, sortType: 'number', render: (row: any) => `NRs. ${row.totalRevenue.toFixed(2)}` },
					{ key: 'totalExpense', header: 'Total Expense', sortable: true, sortType: 'number', render: (row: any) => `NRs. ${row.totalExpense.toFixed(2)}` },
					{
						key: 'netProfit',
						header: 'Net Profit',
						sortable: true,
						sortType: 'number',
						render: (row: any) => {
							const color = row.netProfit >= 0 ? 'var(--p-color-text-success)' : 'var(--p-color-text-critical)';
							return `<span style="font-weight: 600; color: ${color};">NRs. ${row.netProfit.toFixed(2)}</span>`;
						}
					},
					{
						key: 'margin',
						header: 'Margin %',
						sortable: true,
						sortType: 'number',
						render: (row: any) => {
							const margin = row.totalRevenue > 0 ? (row.netProfit / row.totalRevenue * 100) : 0;
							const tone = margin >= 20 ? 'success' : (margin < 0 ? 'critical' : 'attention');
							return `<s-badge tone="${tone}">${margin.toFixed(1)}%</s-badge>`;
						}
					}
				];
				deptsTable.items = report.departments || [];
			}
		}
	}
}

function populateMachineDetailModal(r: any) {
	const contentDiv = document.getElementById('machine-detail-content');
	if (!contentDiv) return;

	const margin = r.totalRevenue > 0 ? (r.netProfit / r.totalRevenue * 100) : 0;
	const profitColor = r.netProfit >= 0 ? 'var(--p-color-text-success)' : 'var(--p-color-text-critical)';
	
	let efficiencyRating = 'Low Profitability';
	let efficiencyTone = 'critical';
	if (margin > 30) {
		efficiencyRating = 'Optimal Margin';
		efficiencyTone = 'success';
	} else if (margin >= 10) {
		efficiencyRating = 'Moderate Margin';
		efficiencyTone = 'info';
	} else if (margin >= 0) {
		efficiencyRating = 'Breakeven / Low Margin';
		efficiencyTone = 'attention';
	}

	// Calculate direct memos by subtracting contract rates
	const directMemosVal = r.directRevenue - r.rentalRevenue - r.contractRevenue;
	const formattedDirectMemos = directMemosVal >= 0 ? directMemosVal.toFixed(2) : '0.00';

	contentDiv.innerHTML = `
		<s-stack gap="base">
			<s-box padding="base" background="surface-secondary" border="base" borderRadius="base">
				<div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem;">
					<div>
						<s-text color="subdued" style="font-size: 0.75rem; text-transform: uppercase;">Machine Serial</s-text>
						<s-heading element="h3" style="margin: 0; font-size: 1.1rem;">${r.serialNumber}</s-heading>
					</div>
					<div>
						<s-text color="subdued" style="font-size: 0.75rem; text-transform: uppercase;">Print Usage</s-text>
						<s-heading element="h3" style="margin: 0; font-size: 1.1rem;">${r.pagesPrinted.toLocaleString()} pages</s-heading>
					</div>
					<div>
						<s-text color="subdued" style="font-size: 0.75rem; text-transform: uppercase;">Allocated Efficiency</s-text>
						<div style="margin-top: 0.2rem;">
							<s-badge tone="${efficiencyTone}">${efficiencyRating}</s-badge>
						</div>
					</div>
				</div>
			</s-box>

			<div class="drilldown-grid">
				<!-- Revenue breakdown -->
				<div>
					<div class="drilldown-title">Revenue Stream Details</div>
					<div class="drilldown-field">
						<span>Contract Rentals (Fixed)</span>
						<strong>NRs. ${r.rentalRevenue.toFixed(2)}</strong>
					</div>
					<div class="drilldown-field">
						<span>Print Usage (Overage)</span>
						<strong>NRs. ${r.contractRevenue.toFixed(2)}</strong>
					</div>
					<div class="drilldown-field">
						<span>Direct Memos</span>
						<strong>NRs. ${formattedDirectMemos}</strong>
					</div>
					<div class="drilldown-field">
						<span>Allocated Memos</span>
						<strong>NRs. ${r.allocatedRevenue.toFixed(2)}</strong>
					</div>
					<div class="drilldown-total">
						<span>Total Machine Revenue</span>
						<span>NRs. ${r.totalRevenue.toFixed(2)}</span>
					</div>
				</div>

				<!-- Expense breakdown -->
				<div>
					<div class="drilldown-title">Expense Stream Details</div>
					<div class="drilldown-field">
						<span>Direct Expenses (Consumables)</span>
						<strong>NRs. ${r.directExpense.toFixed(2)}</strong>
					</div>
					<div class="drilldown-field">
						<span>Allocated Expenses (Shared Cost)</span>
						<strong>NRs. ${r.allocatedExpense.toFixed(2)}</strong>
					</div>
					<div style="height: 38px;"></div> <!-- visual spacer -->
					<div style="height: 38px;"></div> <!-- visual spacer -->
					<div class="drilldown-total">
						<span>Total Machine Expense</span>
						<span>NRs. ${r.totalExpense.toFixed(2)}</span>
					</div>
				</div>
			</div>

			<s-box padding="base" background="surface" border="base" borderRadius="base" style="margin-top: 1rem;">
				<div style="display: flex; justify-content: space-between; align-items: center;">
					<s-heading element="h3">Asset Net Contribution</s-heading>
					<div style="text-align: right;">
						<div style="font-size: 1.25rem; font-weight: 700; color: ${profitColor};">NRs. ${r.netProfit.toFixed(2)}</div>
						<s-text tone="subdued" style="font-size: 0.8rem;">Net Margin: <strong>${margin.toFixed(2)}%</strong></s-text>
					</div>
				</div>
			</s-box>
		</s-stack>
	`;
}

function triggerAppReRender() {
	const ev = new HashChangeEvent('hashchange');
	window.dispatchEvent(ev);
}
